import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { errorMessageKey } from '@/shared/lib/errors';
import { Async } from '@/shared/ui/Async';
import { Button } from '@/shared/ui/Button';
import { StarInput } from '@/shared/ui/StarInput';
import { Stars } from '@/shared/ui/Stars';
import { TextArea } from '@/shared/ui/TextArea';

import { useCreateRating, useMyRatingForApplication } from '../hooks/use-ratings';

/**
 * Shows the caller's rating for a completed application, or a form to leave one.
 * Phase 6 (D-014, D-012): when `readOnlyReason` is set, renders a read-only
 * explanation instead of the form — used on the requester side when the current
 * user is a linked co-manager without the manager role (RLS would reject the
 * insert anyway; this just reflects that truth in the UI).
 */
export function RatingSection({
  applicationId,
  rateeId,
  readOnlyReason,
}: {
  applicationId: string;
  rateeId: string;
  readOnlyReason?: string | null;
}): JSX.Element {
  const { t } = useTranslation();
  const query = useMyRatingForApplication(applicationId);
  const create = useCreateRating(applicationId);
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);

  function submit(): void {
    setError(null);
    if (stars < 1) {
      setError(t('ratings.starsRequired'));
      return;
    }
    create.mutate(
      { applicationId, rateeId, stars, comment: comment.trim() || null },
      { onError: (e) => setError(t(errorMessageKey(e))) },
    );
  }

  return (
    <Async query={query}>
      {(existing) =>
        existing ? (
          <section className="stack">
            <span className="muted">{t('ratings.yourRating')}</span>
            <Stars value={existing.stars} />
            {existing.comment ? <p>{existing.comment}</p> : null}
          </section>
        ) : readOnlyReason ? (
          <section className="stack">
            <h4>{t('ratings.rateTitle')}</h4>
            <p className="muted">{readOnlyReason}</p>
          </section>
        ) : (
          <section className="stack">
            <h4>{t('ratings.rateTitle')}</h4>
            <StarInput value={stars} onChange={setStars} label={t('ratings.rateTitle')} />
            <TextArea
              label={t('ratings.commentLabel')}
              placeholder={t('ratings.commentHint')}
              value={comment}
              maxLength={1000}
              onChange={(e) => setComment(e.target.value)}
            />
            {error ? (
              <p className="text-danger" role="alert">
                {error}
              </p>
            ) : null}
            <Button loading={create.isPending} onClick={submit}>
              {t('ratings.submit')}
            </Button>
          </section>
        )
      }
    </Async>
  );
}
